/**
 * Mobile push notifications (Capacitor native apps only — no-op on the web).
 * Registers the device token with the backend and deep-links notification taps
 * to the right conversation.
 */
import { api } from "../api";

export async function initPushNotifications(userId) {
  try {
    const Cap = window.Capacitor;
    if (!Cap || !Cap.isNativePlatform || !Cap.isNativePlatform()) return;
    const { PushNotifications } = await import("@capacitor/push-notifications");

    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === "prompt" || perm.receive === "prompt-with-rationale") {
      perm = await PushNotifications.requestPermissions();
    }
    if (perm.receive !== "granted") return;

    const platform = Cap.getPlatform(); // 'ios' | 'android'

    if (platform === "android") {
      try {
        // v2 channel: custom "Grave Calmo" sound (channels are immutable once
        // created on the device, so a new id is required for the new sound)
        await PushNotifications.createChannel({
          id: "onsitecomms_v2",
          name: "Messages",
          description: "New messages, mentions and issues",
          importance: 5,
          visibility: 1,
          sound: "notify.wav",
          vibration: true,
        });
      } catch (e) { /* channel may already exist */ }
      try {
        // Buddy reminder ALARM channel (alarm sound + strong vibration).
        // Also created natively by OnSitePushService — same id, first wins.
        await PushNotifications.createChannel({
          id: "buddy_alarm_v1",
          name: "Buddy Reminders (alarm)",
          description: "Buddy reminder alarms",
          importance: 5,
          visibility: 1,
          sound: "buddy_alarm.wav",
          vibration: true,
        });
      } catch (e) { /* channel may already exist */ }
    }

    await PushNotifications.removeAllListeners();

    PushNotifications.addListener("registration", async (t) => {
      try {
        // data_push: this build ships OnSitePushService (native WhatsApp-style
        // notifications with round sender avatar) — backend sends data-only
        await api.post("/push/register", {
          user_id: userId, token: t.value, platform,
          data_push: platform === "android",
        });
      } catch (e) {
        console.warn("push token registration failed", e);
      }
    });

    PushNotifications.addListener("registrationError", (err) => {
      console.warn("push registration error", err);
    });

    // Tapping a notification opens its conversation; the inline "Reply" action
    // (long-press → type → send, WhatsApp-style) posts the reply straight to
    // the API via the signed reply_token embedded in the push payload.
    // NOTE: on iOS the reply is normally handled NATIVELY (AppDelegate proxy)
    // and never reaches this listener — this is the safety-net fallback.
    PushNotifications.addListener("pushNotificationActionPerformed", async (action) => {
      const data = action?.notification?.data || {};
      const convId = data.conversation_id;
      // MASTER flyer tapped → open it fullscreen in the app
      if (data.broadcast_id) {
        try { localStorage.setItem("osc_open_broadcast", String(data.broadcast_id)); } catch (e) { /* ignore */ }
        try { window.dispatchEvent(new CustomEvent("osc-open-broadcast", { detail: { id: data.broadcast_id } })); } catch (e) { /* ignore */ }
        return;
      }
      // Buddy reminder tapped → open the app RINGING (full-screen alarm overlay)
      if (data.buddy_reminder === "1" || data.buddy_reminder === 1) {
        const txt = data.text || action?.notification?.body || "";
        try {
          // survives cold start: ReminderAlarm checks this key on mount
          localStorage.setItem("osc_ring_reminder", JSON.stringify({ text: txt, ts: Date.now() }));
        } catch (e) { /* ignore */ }
        try {
          window.dispatchEvent(new CustomEvent("osc-user-notify", {
            detail: { inner: "buddy_reminder", text: txt },
          }));
        } catch (e) { /* ignore */ }
        return; // no navigation — the alarm overlay covers the screen
      }
      if (action?.actionId === "REPLY_ACTION") {
        const text = (action?.inputValue || "").trim();
        if (text && data.reply_token) {
          try {
            await api.post("/push/reply", { token: data.reply_token, text });
          } catch (e) {
            console.warn("push inline reply failed", e);
          }
        }
        if (convId) clearConversationNotifications(convId);
        return; // background action — do not navigate
      }
      // New account request (admins) → open the Admin panel on the Users tab
      if (data.account_request === "1" || data.account_request === 1) {
        window.location.href = "/admin";
        return;
      }
      if (convId) {
        // Issue push: the app opens with the floating ⚠️ balloon already open
        if (data.floating_issue === "1" || data.floating_issue === 1) {
          try { localStorage.setItem("osc_open_issue_balloon", "1"); } catch (e) { /* ignore */ }
        }
        // Opening one notification dismisses the OTHERS from the same
        // conversation only — notifications from other people/groups stay.
        clearConversationNotifications(convId);
        window.location.href = `/chat/${convId}`;
      } else if (data.floating_issue === "1" || data.floating_issue === 1) {
        try { localStorage.setItem("osc_open_issue_balloon", "1"); } catch (e) { /* ignore */ }
        window.location.href = "/home";
      }
    });

    // Push arrived with the app in the FOREGROUND: acknowledge delivery right
    // away (redundant with the iOS NSE — layers, not assumptions)
    PushNotifications.addListener("pushNotificationReceived", (n) => {
      const data = n?.data || {};
      if (data.delivery_token) {
        api.post("/push/delivered", { token: data.delivery_token }).catch(() => {});
      }
    });

    await PushNotifications.register();
  } catch (e) {
    console.warn("push init skipped:", e?.message || e);
  }
}

/**
 * Remove every delivered notification belonging to ONE conversation from the
 * notification center (called on tap, on inline reply and when the thread is
 * opened in-app). Other conversations' notifications are left untouched.
 */
export async function clearConversationNotifications(conversationId) {
  try {
    const Cap = window.Capacitor;
    if (!Cap || !Cap.isNativePlatform || !Cap.isNativePlatform() || !conversationId) return;
    const { PushNotifications } = await import("@capacitor/push-notifications");
    const { notifications } = await PushNotifications.getDeliveredNotifications();
    const mine = (notifications || []).filter(
      (n) => (n?.data?.conversation_id || "") === conversationId
    );
    if (mine.length) {
      await PushNotifications.removeDeliveredNotifications({ notifications: mine });
    }
  } catch (e) { /* best effort — notification center cleanup only */ }
}
